import {
    MouseEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    ChevronLeft,
    ChevronRight,
    Expand,
    Image as ImageIcon,
    Play,
    Share2,
    X,
} from "lucide-react";
import "./ProductImageGallery.css";

type GalleryImage = {
    src: string;
    position?: string;
    scale?: number;
    label?: string;
};

type ProductImageGalleryProps = {
    title: string;
    image?: string;
    images?: string[];
    videos?: string[];
    description?: string;
    color?: string;
    size?: string;
    variant?: string;
};

type GalleryTab = "images" | "videos";

const ProductImageGallery = ({
    title,
    image,
    images = [],
    videos = [],
    description = "-",
    color = "-",
    size = "-",
    variant = "-",
}: ProductImageGalleryProps) => {
    const displayImageSources = useMemo(() => {
        const list = (images || []).filter(
            (src): src is string =>
                Boolean(src) &&
                typeof src === "string" &&
                src.trim() !== ""
        );

        if (list.length === 0 && image) {
            return [image];
        }

        return list;
    }, [images, image]);

    const galleryImages =
        useMemo<GalleryImage[]>(
            () => {
                if (displayImageSources.length > 1) {
                    return displayImageSources.map(
                        (src, index) => ({
                            src,
                            position: "center",
                            scale: 1,
                            label: `${title} view ${index + 1}`,
                        })
                    );
                }

                if (displayImageSources.length === 1) {
                    const base = displayImageSources[0];

                    return [
                        {
                            src: base,
                            position: "center",
                            scale: 1,
                            label: `${title} full view`,
                        },
                        {
                            src: base,
                            position: "center top",
                            scale: 1.2,
                            label: `${title} upper view`,
                        },
                        {
                            src: base,
                            position: "center",
                            scale: 1.35,
                            label: `${title} detail view`,
                        },
                        {
                            src: base,
                            position: "center 35%",
                            scale: 1.5,
                            label: `${title} close view`,
                        },
                        {
                            src: base,
                            position: "center bottom",
                            scale: 1.3,
                            label: `${title} lower view`,
                        },
                        {
                            src: base,
                            position: "center",
                            scale: 1.1,
                            label: `${title} alternate view`,
                        },
                    ];
                }

                return [];
            },
            [
                displayImageSources,
                title,
            ]
        );

    const [
        selectedIndex,
        setSelectedIndex,
    ] = useState(0);

    const [
        modalSelectedIndex,
        setModalSelectedIndex,
    ] = useState(0);

    useEffect(() => {
        if (galleryImages.length === 0) {
            setSelectedIndex(0);
        } else if (selectedIndex >= galleryImages.length) {
            setSelectedIndex(0);
        }
    }, [galleryImages.length, selectedIndex]);

    useEffect(() => {
        if (galleryImages.length === 0) {
            setModalSelectedIndex(0);
        } else if (modalSelectedIndex >= galleryImages.length) {
            setModalSelectedIndex(0);
        }
    }, [galleryImages.length, modalSelectedIndex]);

    const [
        zoomActive,
        setZoomActive,
    ] = useState(false);

    const [
        zoomPosition,
        setZoomPosition,
    ] = useState({
        x: 50,
        y: 50,
    });

    const [
        modalOpen,
        setModalOpen,
    ] = useState(false);

    const [
        activeTab,
        setActiveTab,
    ] = useState<GalleryTab>(
        "images"
    );

    const [
        selectedVideoIndex,
        setSelectedVideoIndex,
    ] = useState(0);

    const [
        mobileSelectedIndex,
        setMobileSelectedIndex,
    ] = useState(0);

    const mobileScrollRef =
        useRef<HTMLDivElement>(null);

    const hoverTimerRef =
        useRef<
            ReturnType<typeof setTimeout> | null
        >(null);

    const selectedImage =
        galleryImages[selectedIndex] || null;

    const modalSelectedImage =
        galleryImages[modalSelectedIndex] || null;

    useEffect(() => {
        setZoomActive(false);

        setZoomPosition({
            x: 50,
            y: 50,
        });

        if (hoverTimerRef.current) {
            clearTimeout(
                hoverTimerRef.current
            );

            hoverTimerRef.current = null;
        }
    }, [selectedIndex]);

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(
                    hoverTimerRef.current
                );
            }
        };
    }, []);

    useEffect(() => {
        if (!modalOpen) {
            return;
        }

        const previousOverflow =
            document.body.style.overflow;

        document.body.style.overflow =
            "hidden";

        const handleKeyDown = (
            event: KeyboardEvent
        ) => {
            if (event.key === "Escape") {
                setModalOpen(false);
            }

            if (activeTab !== "images") {
                return;
            }

            if (event.key === "ArrowLeft") {
                if (galleryImages.length > 1) {
                    setModalSelectedIndex(
                        (prev) =>
                            prev === 0
                                ? galleryImages.length - 1
                                : prev - 1
                    );
                }
            }

            if (event.key === "ArrowRight") {
                if (galleryImages.length > 1) {
                    setModalSelectedIndex(
                        (prev) =>
                            prev === galleryImages.length - 1
                                ? 0
                                : prev + 1
                    );
                }
            }
        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {
            document.body.style.overflow =
                previousOverflow;

            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };
    }, [
        modalOpen,
        activeTab,
        galleryImages.length,
    ]);

    const handleMouseEnter = () => {
        if (hoverTimerRef.current) {
            clearTimeout(
                hoverTimerRef.current
            );
        }

        hoverTimerRef.current =
            setTimeout(() => {
                setZoomActive(true);
            }, 750);
    };

    const handleMouseLeave = () => {
        if (hoverTimerRef.current) {
            clearTimeout(
                hoverTimerRef.current
            );

            hoverTimerRef.current = null;
        }

        setZoomActive(false);

        setZoomPosition({
            x: 50,
            y: 50,
        });
    };

    const handleMouseMove = (
        event: MouseEvent<HTMLDivElement>
    ) => {
        if (!zoomActive) {
            return;
        }

        const rect =
            event.currentTarget.getBoundingClientRect();

        const x =
            ((event.clientX - rect.left) /
                rect.width) *
            100;

        const y =
            ((event.clientY - rect.top) /
                rect.height) *
            100;

        setZoomPosition({
            x: Math.max(
                0,
                Math.min(100, x)
            ),
            y: Math.max(
                0,
                Math.min(100, y)
            ),
        });
    };

    const handleShare = async () => {
        const shareData = {
            title,
            text: `Check out ${title} on Salar`,
            url: window.location.href,
        };

        try {
            if (navigator.share) {
                await navigator.share(
                    shareData
                );
            } else {
                await navigator.clipboard.writeText(
                    window.location.href
                );
            }
        } catch {
            return;
        }
    };

    const handleModalPreviousImage = () => {
        if (galleryImages.length <= 1) return;
        setModalSelectedIndex(
            (prev) =>
                prev === 0
                    ? galleryImages.length - 1
                    : prev - 1
        );
    };

    const handleModalNextImage = () => {
        if (galleryImages.length <= 1) return;
        setModalSelectedIndex(
            (prev) =>
                prev === galleryImages.length - 1
                    ? 0
                    : prev + 1
        );
    };

    const handleOpenModal = () => {
        setActiveTab("images");
        setModalSelectedIndex(selectedIndex);
        setModalOpen(true);
    };

    const handleMobileScroll = () => {
        const container =
            mobileScrollRef.current;

        if (!container) {
            return;
        }

        const width =
            container.clientWidth;

        if (!width) {
            return;
        }

        const nextIndex =
            Math.round(
                container.scrollLeft /
                width
            );

        setMobileSelectedIndex(
            Math.max(
                0,
                Math.min(
                    galleryImages.length - 1,
                    nextIndex
                )
            )
        );
    };

    const handleMobileDotClick = (
        index: number
    ) => {
        const container =
            mobileScrollRef.current;

        if (!container) {
            return;
        }

        container.scrollTo({
            left:
                index *
                container.clientWidth,
            behavior: "smooth",
        });
    };

    return (
        <>
            <section className="product-gallery">
                <div className="product-gallery-desktop">
                    <div
                        className={`product-gallery-main ${zoomActive
                            ? "zoom-active"
                            : ""
                            }`}
                    onMouseEnter={
                        handleMouseEnter
                    }
                    onMouseLeave={
                        handleMouseLeave
                    }
                    onMouseMove={
                        handleMouseMove
                    }
                >
                    <div className="product-gallery-tools">
                        <button
                            type="button"
                            className="product-gallery-tool"
                            onClick={
                                handleOpenModal
                            }
                            aria-label="Open product gallery"
                        >
                            <Expand
                                size={17}
                                strokeWidth={1.8}
                            />
                        </button>

                        <button
                            type="button"
                            className="product-gallery-tool"
                            onClick={
                                handleShare
                            }
                            aria-label="Share product"
                        >
                            <Share2
                                size={17}
                                strokeWidth={1.8}
                            />
                        </button>
                    </div>

                    <div className="product-gallery-main-image">
                        {selectedImage?.src ? (
                            <img
                                src={
                                    selectedImage.src
                                }
                                alt={
                                    selectedImage.label ||
                                    title
                                }
                                style={{
                                    objectPosition:
                                        selectedImage.position,

                                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,

                                    transform:
                                        zoomActive
                                            ? `scale(${Math.max(
                                                2.2,
                                                (selectedImage.scale ??
                                                    1) *
                                                2.2
                                            )})`
                                            : `scale(${selectedImage.scale ??
                                            1
                                            })`,
                                }}
                            />
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#9ca3af", gap: 8 }}>
                                <ImageIcon size={44} strokeWidth={1.2} />
                                <span style={{ fontSize: 13 }}>-</span>
                            </div>
                        )}
                    </div>

                    <div className="product-gallery-counter">
                        {galleryImages.length > 0
                            ? `${selectedIndex + 1} / ${galleryImages.length}`
                            : "-"}
                    </div>
                </div>

                {galleryImages.length > 0 && (
                    <div className="product-gallery-thumbnail-scroll">
                        <div className="product-gallery-thumbnails">
                            {galleryImages.map(
                                (
                                    item,
                                    index
                                ) => (
                                    <button
                                        type="button"
                                        key={`${item.src}-${index}`}
                                        className={`product-gallery-thumbnail ${selectedIndex ===
                                            index
                                            ? "active"
                                            : ""
                                            }`}
                                        onClick={() =>
                                            setSelectedIndex(
                                                index
                                            )
                                        }
                                    >
                                        <span>
                                            <img
                                                src={
                                                    item.src
                                                }
                                                alt={`${title} view ${index + 1
                                                    }`}
                                                style={{
                                                    objectPosition:
                                                        item.position,

                                                    transform: `scale(${item.scale ??
                                                        1
                                                        })`,
                                                }}
                                            />
                                        </span>
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                )}
                </div>

                {galleryImages.length > 0 && (
                    <div className="product-gallery-mobile">
                        <div className="product-gallery-mobile-stage">
                            <div className="product-gallery-mobile-tools">
                                <button
                                    type="button"
                                    className="product-gallery-mobile-tool"
                                    onClick={handleOpenModal}
                                    aria-label="Zoom product image"
                                >
                                    <Expand
                                        size={18}
                                        strokeWidth={1.9}
                                    />
                                </button>

                                <button
                                    type="button"
                                    className="product-gallery-mobile-tool"
                                    onClick={handleShare}
                                    aria-label="Share product"
                                >
                                    <Share2
                                        size={18}
                                        strokeWidth={1.9}
                                    />
                                </button>
                            </div>

                            <div
                                ref={mobileScrollRef}
                                className="product-gallery-mobile-scroll"
                                onScroll={handleMobileScroll}
                            >
                                {galleryImages.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <div
                                            className="product-gallery-mobile-slide"
                                            key={`mobile-${item.src}-${index}`}
                                        >
                                            <img
                                                src={item.src}
                                                alt={
                                                    item.label ||
                                                    `${title} view ${index + 1}`
                                                }
                                                style={{
                                                    objectPosition:
                                                        item.position ||
                                                        "center",
                                                }}
                                            />
                                        </div>
                                    )
                                )}
                            </div>

                            <div className="product-gallery-mobile-counter">
                                {mobileSelectedIndex + 1}
                                <span>/</span>
                                {galleryImages.length}
                            </div>
                        </div>

                        {galleryImages.length > 1 && (
                            <div
                                className="product-gallery-mobile-dots"
                                aria-label="Product image position"
                            >
                                {galleryImages.map(
                                    (
                                        _item,
                                        index
                                    ) => (
                                        <button
                                            key={`mobile-dot-${index}`}
                                            type="button"
                                            className={
                                                mobileSelectedIndex ===
                                                    index
                                                    ? "active"
                                                    : ""
                                            }
                                            onClick={() =>
                                                handleMobileDotClick(
                                                    index
                                                )
                                            }
                                            aria-label={`Go to product image ${index + 1
                                                }`}
                                        />
                                    )
                                )}
                            </div>
                        )}
                    </div>
                )}
            </section>

            {modalOpen && (
                <div
                    className="product-gallery-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${title} gallery`}
                >
                    <div
                        className="product-gallery-modal-backdrop"
                        onClick={() =>
                            setModalOpen(false)
                        }
                    />

                    <div className="product-gallery-modal-card">
                        <div className="product-gallery-modal-top">
                            <div>
                                <span>
                                    PRODUCT MEDIA
                                </span>

                                <strong>
                                    Salar Gallery
                                </strong>
                            </div>

                            <button
                                type="button"
                                className="product-gallery-modal-close"
                                onClick={() =>
                                    setModalOpen(false)
                                }
                            >
                                <X
                                    size={18}
                                    strokeWidth={2}
                                />

                                Close
                            </button>
                        </div>

                        <div className="product-gallery-modal-stage">
                            {activeTab ===
                                "images" ? (
                                <>
                                    {galleryImages.length >
                                        1 && (
                                            <button
                                                type="button"
                                                className="product-gallery-modal-arrow left"
                                                onClick={
                                                    handleModalPreviousImage
                                                }
                                            >
                                                <ChevronLeft
                                                    size={22}
                                                />
                                            </button>
                                        )}

                                    <div className="product-gallery-modal-main-image">
                                        {modalSelectedImage?.src ? (
                                            <img
                                                src={
                                                    modalSelectedImage.src
                                                }
                                                alt={
                                                    modalSelectedImage.label ||
                                                    title
                                                }
                                            />
                                        ) : (
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#9ca3af", gap: 8 }}>
                                                <ImageIcon size={64} strokeWidth={1.2} />
                                                <span style={{ fontSize: 14 }}>-</span>
                                            </div>
                                        )}
                                    </div>

                                    {galleryImages.length >
                                        1 && (
                                            <button
                                                type="button"
                                                className="product-gallery-modal-arrow right"
                                                onClick={
                                                    handleModalNextImage
                                                }
                                            >
                                                <ChevronRight
                                                    size={22}
                                                />
                                            </button>
                                        )}

                                    <span className="product-gallery-modal-image-count">
                                        {galleryImages.length > 0
                                            ? `${modalSelectedIndex + 1} / ${galleryImages.length}`
                                            : "-"}
                                    </span>
                                </>
                            ) : videos.length >
                                0 ? (
                                <video
                                    key={
                                        videos[
                                        selectedVideoIndex
                                        ]
                                    }
                                    className="product-gallery-modal-video"
                                    src={
                                        videos[
                                        selectedVideoIndex
                                        ]
                                    }
                                    controls
                                    playsInline
                                />
                            ) : (
                                <div className="product-gallery-modal-empty-video">
                                    <span>
                                        <Play
                                            size={23}
                                        />
                                    </span>

                                    <strong>
                                        No product videos
                                    </strong>

                                    <p>
                                        Video content is
                                        not available for
                                        this product yet.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="product-gallery-modal-bottom">
                            <div className="product-gallery-modal-tabs">
                                <button
                                    type="button"
                                    className={
                                        activeTab ===
                                            "images"
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setActiveTab(
                                            "images"
                                        )
                                    }
                                >
                                    <ImageIcon
                                        size={14}
                                    />

                                    Images

                                    <span>
                                        {
                                            galleryImages.length
                                        }
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    className={
                                        activeTab ===
                                            "videos"
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setActiveTab(
                                            "videos"
                                        )
                                    }
                                >
                                    <Play
                                        size={13}
                                    />

                                    Videos

                                    <span>
                                        {
                                            videos.length
                                        }
                                    </span>
                                </button>
                            </div>

                            {activeTab ===
                                "images" ? (
                                galleryImages.length > 0 ? (
                                    <div className="product-gallery-modal-thumbnails">
                                        {galleryImages.map(
                                            (
                                                item,
                                                index
                                            ) => (
                                                <button
                                                    type="button"
                                                    key={`modal-${item.src}-${index}`}
                                                    className={
                                                        modalSelectedIndex ===
                                                            index
                                                            ? "active"
                                                            : ""
                                                    }
                                                    onClick={() =>
                                                        setModalSelectedIndex(
                                                            index
                                                        )
                                                    }
                                                >
                                                    <img
                                                        src={
                                                            item.src
                                                        }
                                                        alt={`${title} ${index +
                                                            1
                                                            }`}
                                                    />
                                                </button>
                                            )
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ padding: "16px", color: "#9ca3af", fontSize: 13, textAlign: "center" }}>-</div>
                                )
                            ) : (
                                videos.length >
                                0 && (
                                    <div className="product-gallery-modal-video-thumbnails">
                                        {videos.map(
                                            (
                                                video,
                                                index
                                            ) => (
                                                <button
                                                    type="button"
                                                    key={`${video}-${index}`}
                                                    className={
                                                        selectedVideoIndex ===
                                                            index
                                                            ? "active"
                                                            : ""
                                                    }
                                                    onClick={() =>
                                                        setSelectedVideoIndex(
                                                            index
                                                        )
                                                    }
                                                >
                                                    <Play
                                                        size={13}
                                                    />

                                                    Video{" "}
                                                    {index +
                                                        1}
                                                </button>
                                            )
                                        )}
                                    </div>
                                )
                            )}

                            <div className="product-gallery-modal-info">
                                <div className="product-gallery-modal-title">
                                    <h2>
                                        {title}
                                    </h2>

                                    <p>
                                        {
                                            description
                                        }
                                    </p>
                                </div>

                                <div className="product-gallery-modal-variants">
                                    <div>
                                        <span>
                                            Colour
                                        </span>

                                        <strong>
                                            {color}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Size
                                        </span>

                                        <strong>
                                            {size}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Variant
                                        </span>

                                        <strong>
                                            {variant}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProductImageGallery;